import type {Labeled, Valuable} from 'src/typings';
import {base64ToUtf, utfToBase64} from 'shared/utils/string-utils';
import {exists, isFunction} from 'shared/utils/custom-utils';
import {join, matrixMap} from 'shared/utils/array-utils';

export type ClipboardCellData = Labeled & Valuable<Record<string, any>>;

export function createClipBoardItem(matrixOfCellData: ClipboardCellData[][]): ClipboardItem {
    const textBlob = createPlainTextBlob(matrixOfCellData);
    const htmlBlob = createHtmlBlob(matrixOfCellData);
    const jsonBlob = createJsonBlob(matrixOfCellData);

    return new ClipboardItem({
        [textBlob.type]: textBlob,
        [htmlBlob.type]: htmlBlob,
        ...supportsCustomWebFormats() ? {[jsonBlob.type]: jsonBlob} : {},
    });
}


export async function resolveClipboardData(clipboardItems: ClipboardItems): Promise<ClipboardCellData['text'][][] | ClipboardCellData['value'][][]> {
    let clipboardItem = clipboardItems.find((item) => item.types.includes('web application/json'));
    if (exists(clipboardItem)) {
        return parseJsonBlob(clipboardItem);
    }

    clipboardItem = clipboardItems.find((item) => item.types.includes('text/html'));
    if (exists(clipboardItem)) {
        return parseHtmlBlob(clipboardItem);
    }

    clipboardItem = clipboardItems.find((item) => item.types.includes('text/plain'));
    if (exists(clipboardItem)) {
        return parsePlainTextBlob(clipboardItem);
    }

    return null;
}

function formatAsPlainText(matrixOfCellData: ClipboardCellData[][]): string {
    const cellsAsText = matrixMap(matrixOfCellData, ({text}) => text);
    const plainTextRows = cellsAsText.map((cellsInRow) => join(cellsInRow, '\t'));
    return join(plainTextRows, '\n');
}

function formatAsHtml(matrixOfCellData: ClipboardCellData[][]): string {
    const cellsAsHtmlStrings = matrixMap(matrixOfCellData, formatClipboardCellDataAsHtmlTableCell);
    const rowsAsHtml = cellsAsHtmlStrings.map((cellsInRow) => join(cellsInRow, '\n'));
    const textValue = join(rowsAsHtml, '\n', '<tr>', '</tr>');
    return `<table class="data-grid">${textValue}</table>`;
}

// use DOM api instead of string template for built-in escaping and sanitizing
function formatClipboardCellDataAsHtmlTableCell(clipboardCellData: ClipboardCellData): string {
    const json = JSON.stringify(clipboardCellData.value);
    const base64 = utfToBase64(json);
    const cellElement = document.createElement('td');
    cellElement.className = 'data-grid-cell';
    cellElement.dataset.value = base64;
    cellElement.textContent = clipboardCellData.text;
    return cellElement.outerHTML;
}

function formatAsJson(matrixOfCellData: ClipboardCellData[][]): string {
    const cellValues = matrixMap(matrixOfCellData, ({value}) => value);
    return JSON.stringify(cellValues);
}

function createPlainTextBlob(matrixOfCellData: ClipboardCellData[][]) {
    const plainText = formatAsPlainText(matrixOfCellData);
    return new Blob([plainText], {type: 'text/plain'});
}

function createJsonBlob(matrixOfCellData: ClipboardCellData[][]) {
    const json = formatAsJson(matrixOfCellData);
    return new Blob([json], {type: 'web application/json'});
}

function createHtmlBlob(matrixOfCellData: ClipboardCellData[][]) {
    const html = formatAsHtml(matrixOfCellData);
    return new Blob([html], {type: 'text/html'});
}

function supportsCustomWebFormats() {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    return 'supports' in ClipboardItem && isFunction(ClipboardItem.supports) && ClipboardItem.supports('web application/json');
}

async function parsePlainTextBlob(clipboardItem: ClipboardItem): Promise<ClipboardCellData['text'][][]> {
    const textBlob = await clipboardItem.getType('text/plain');
    const plainText = await textBlob.text();
    const rows = plainText.split('\n');
    const cells = rows.map((row) => row.split('\t'));
    // TODO: make some checks and asserts
    return cells;
}

async function parseJsonBlob(clipboardItem: ClipboardItem): Promise<ClipboardCellData['value'][][]> {
    const jsonBlob = await clipboardItem.getType('web application/json');
    const json = await jsonBlob.text();
    return JSON.parse(json);
}

async function parseHtmlBlob(clipboardItem: ClipboardItem): Promise<ClipboardCellData['value'][][]> {
    const jsonBlob = await clipboardItem.getType('text/html');
    const html = await jsonBlob.text();
    const parser = new DOMParser();
    const container = parser.parseFromString(html, 'text/html');
    const tableElement = container.querySelector('table.data-grid');
    const rowElements = [...tableElement.querySelectorAll('tr')];
    const cellElements = rowElements.map((rowElement) => [...rowElement.querySelectorAll('td')]);
    return matrixMap(cellElements, (cellElement) => JSON.parse(base64ToUtf(cellElement.dataset.value)))
}